<template>
  <b-card
    no-body
    class="case-card"
  >
    <!-- Card Header with gradient -->
    <div class="case-card-header">
      <div class="case-header-content">
        <div class="creditor-name">
          {{ caseData.creditorName }}
        </div>
        <div class="journal-number">
          {{ $t('DEBTIA_CASE_PREFIX') }}: {{ caseData.journalNumber }}
        </div>
      </div>
      <div class="header-actions">
        <b-button
          v-if="!isClosed"
          variant="link"
          size="sm"
          class="message-btn"
          :to="{ path: '/messages', query: { caseId: caseData.id } }"
        >
          <feather-icon
            icon="MailIcon"
            size="16"
          />
        </b-button>
        <b-badge
          :variant="statusVariant"
          class="case-status-badge"
        >
          {{ statusText }}
        </b-badge>
      </div>
    </div>

    <!-- Card Body -->
    <b-card-body class="case-card-body">
      <!-- Empty statements: show informational message -->
      <template v-if="caseData.statementsEmpty">
        <div class="statements-pending">
          {{ $t('DEBTIA_STATEMENTS_PENDING') }}
        </div>
      </template>

      <template v-else>
        <div class="amount-row">
          <span class="amount-label">{{ $t('DEBTIA_PRINCIPAL') }}</span>
          <span class="amount-value">{{
            formatCurrency(caseData.principal)
          }}</span>
        </div>
        <div class="amount-row">
          <span class="amount-label">{{ $t('DEBTIA_INTEREST') }}</span>
          <span class="amount-value">{{
            formatCurrency(caseData.interest)
          }}</span>
        </div>
        <div class="amount-row">
          <span class="amount-label">{{ $t('DEBTIA_FEES') }}</span>
          <span class="amount-value">{{
            formatCurrency(caseData.fees)
          }}</span>
        </div>

        <hr class="amount-divider">

        <!-- Closed case: fully paid -->
        <template v-if="isClosed">
          <div class="amount-row">
            <span class="amount-label">{{ $t('DEBTIA_TOTAL_TO_PAY') }}</span>
            <span class="amount-value">{{
              formatCurrency(caseData.totalAmount)
            }}</span>
          </div>
          <div class="amount-row">
            <span class="amount-label">{{ $t('DEBTIA_ALREADY_PAID') }}</span>
            <span class="amount-value text-success">{{
              formatCurrencyNegative(caseData.paidAmount || caseData.totalAmount)
            }}</span>
          </div>
          <div class="amount-row total-row">
            <span class="amount-label">{{ $t('DEBTIA_REMAINING_TO_PAY') }}</span>
            <span class="amount-value text-success">{{
              formatCurrency(caseData.remainingAmount || 0)
            }}</span>
          </div>
        </template>

        <!-- Payment Plan case -->
        <template v-else-if="caseData.hasPaymentPlan">
          <div class="amount-row">
            <span class="amount-label">{{ $t('DEBTIA_TOTAL_TO_PAY') }}</span>
            <span class="amount-value">{{
              formatCurrency(caseData.totalAmount)
            }}</span>
          </div>
          <div class="amount-row">
            <span class="amount-label">
              {{ $t('DEBTIA_ALREADY_PAID') }}
              <span class="payment-plan-label">({{ $t('DEBTIA_STATUS_PAYMENT_PLAN') }})</span>
              <span
                :id="`tooltip-${caseData.id}`"
                class="info-icon"
              >
                <feather-icon
                  icon="InfoIcon"
                  size="14"
                />
              </span>
              <b-tooltip
                :target="`tooltip-${caseData.id}`"
                triggers="hover"
                placement="bottom"
                boundary="viewport"
              >
                <div class="tooltip-content">
                  <div>
                    {{ caseData.paidInstallments || 0 }}/{{
                      caseData.totalInstallments || 0
                    }}
                    {{ $t('DEBTIA_INSTALLMENTS_PAID') }}
                  </div>
                  <div>
                    {{ $t('DEBTIA_NEXT_INSTALLMENT') }}:
                    {{
                      caseData.nextPaymentDate ||
                        $t('DEBTIA_NOT_SET')
                    }}
                  </div>
                </div>
              </b-tooltip>
            </span>
            <span class="amount-value text-success">{{
              formatCurrencyNegative(caseData.paidAmount || 0)
            }}</span>
          </div>
          <div class="amount-row total-row">
            <span class="amount-label">{{ $t('DEBTIA_REMAINING_TO_PAY') }}</span>
            <span class="amount-value text-danger">{{
              formatCurrency(
                caseData.remainingAmount || caseData.totalAmount
              )
            }}</span>
          </div>
        </template>

        <!-- Active case without payment plan -->
        <template v-else>
          <div class="amount-row">
            <span class="amount-label">{{ $t('DEBTIA_TOTAL_TO_PAY') }}</span>
            <span class="amount-value">{{
              formatCurrency(caseData.totalAmount)
            }}</span>
          </div>
          <div class="amount-row">
            <span class="amount-label">{{ $t('DEBTIA_ALREADY_PAID') }}</span>
            <span class="amount-value text-success">{{
              formatCurrencyNegative(caseData.paidAmount || 0)
            }}</span>
          </div>
          <div class="amount-row total-row">
            <span class="amount-label">{{ $t('DEBTIA_REMAINING_TO_PAY') }}</span>
            <span class="amount-value text-danger">{{
              formatCurrency(
                caseData.remainingAmount || caseData.totalAmount
              )
            }}</span>
          </div>
        </template>
      </template>
    </b-card-body>

    <!-- Card Footer for closed cases -->
    <div
      v-if="isClosed"
      class="case-card-footer closed-footer"
    >
      <div class="closed-label">
        <feather-icon
          icon="CheckCircleIcon"
          size="16"
          class="mr-50"
        />
        {{ $t('DEBTIA_CASE_CLOSED') }}
      </div>
    </div>

    <!-- Card Footer - only show for non-closed cases -->
    <div
      v-else
      class="case-card-footer"
    >
      <b-button
        variant="primary"
        class="action-btn"
        :disabled="loading"
        @click="handleFullPayment"
      >
        <b-spinner
          v-if="loading && loadingType === 'full'"
          small
          class="mr-50"
        />
        <feather-icon
          v-else
          icon="CreditCardIcon"
          class="mr-50"
        />
        {{ $t('DEBTIA_FULL_PAYMENT') }}
      </b-button>
      <b-button
        v-if="!caseData.hasPaymentPlan"
        variant="outline-primary"
        class="action-btn"
        :disabled="loading"
        @click="handleCreatePlan"
      >
        <b-spinner
          v-if="loading && loadingType === 'plan'"
          small
          class="mr-50"
        />
        <feather-icon
          v-else
          icon="CalendarIcon"
          class="mr-50"
        />
        {{ $t('DEBTIA_NEW_PAYMENT_PLAN') }}
      </b-button>
      <b-button
        v-else
        variant="outline-success"
        class="action-btn"
        @click="$emit('view-plan', caseData)"
      >
        <feather-icon
          icon="EyeIcon"
          class="mr-50"
        />
        {{ $t('DEBTIA_VIEW_PAYMENT_PLAN') }}
      </b-button>
    </div>
  </b-card>
</template>

<script>
import {
  BCard, BCardBody, BButton, BBadge, BTooltip, BSpinner,
} from 'bootstrap-vue'

export default {
  name: 'CaseCard',
  components: {
    BCard,
    BCardBody,
    BButton,
    BBadge,
    BTooltip,
    BSpinner,
  },
  props: {
    caseData: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingType: {
      type: String,
      default: null,
    },
  },
  computed: {
    isClosed() {
      return this.caseData.status === 'closed'
    },
    statusVariant() {
      const variants = {
        active: 'light-primary',
        hold: 'light-warning',
        payment_plan: 'light-info',
        closed: 'light-success',
      }
      return variants[this.caseData.status] || 'light-secondary'
    },
    statusText() {
      const texts = {
        active: this.$t('DEBTIA_STATUS_ACTIVE'),
        hold: this.$t('DEBTIA_STATUS_HOLD'),
        payment_plan: this.$t('DEBTIA_STATUS_PAYMENT_PLAN'),
        closed: this.$t('DEBTIA_STATUS_CLOSED'),
      }
      return texts[this.caseData.status] || this.$t('DEBTIA_STATUS_UNKNOWN')
    },
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) return '—'
      return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: 'DKK',
        minimumFractionDigits: 2,
      }).format(value)
    },
    formatCurrencyNegative(value) {
      if (!value && value !== 0) return '—'
      const formatted = new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: 'DKK',
        minimumFractionDigits: 2,
      }).format(value)
      return `-${formatted}`
    },
    handleFullPayment() {
      this.$emit('full-payment', this.caseData)
    },
    handleCreatePlan() {
      this.$emit('create-plan', this.caseData)
    },
  },
}
</script>

<style lang="scss" scoped>
.case-card {
    overflow: hidden;
    width: 100%;
    height: 100%;
    margin: 0;

    .case-card-header {
        --_gradient:
            radial-gradient(
                ellipse 70% 200% at 10% 10%,
                #088c76 0%,
                transparent 60%
            ),
            radial-gradient(
                ellipse 100% 140% at 80% 30%,
                #002954 0%,
                transparent 100%
            );
        background: var(--_gradient);
        background-color: #000000;
        padding: 1rem 1.25rem;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 0.5rem;

        .case-header-content {
            flex: 1;
            min-width: 0;

            .creditor-name {
                font-size: 1.125rem;
                font-weight: 600;
                color: #ffffff;
                margin-bottom: 0.25rem;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .journal-number {
                font-size: 0.79rem;
                color: rgba(255, 255, 255, 0.7);
            }
        }

        .header-actions {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            flex-shrink: 0;

            .message-btn {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                padding: 0.4rem 0.5rem;
                background: rgba(255, 255, 255, 0.15) !important;
                border: 1px solid rgba(255, 255, 255, 0.4) !important;
                color: #ffffff !important;
                text-decoration: none;
                border-radius: 0.25rem;

                &:hover {
                    background: rgba(255, 255, 255, 0.3) !important;
                }

                svg {
                    flex-shrink: 0;
                    width: 14px;
                    height: 14px;
                }
            }

            .case-status-badge {
                font-size: 0.72rem;
                font-weight: 600;
                padding: 0.4rem 0.7rem;
                letter-spacing: 0.02em;
                background: rgba(255, 255, 255, 0.2) !important;
                border: 1px solid rgba(255, 255, 255, 0.4) !important;
                color: #ffffff !important;
            }

            .case-status-badge.badge-light-primary {
                background: rgba(40, 199, 111, 0.35) !important;
                border-color: rgba(40, 199, 111, 0.6) !important;
                color: #ffffff !important;
            }

            .case-status-badge.badge-light-warning {
                background: rgba(255, 159, 67, 0.3) !important;
                border-color: rgba(255, 159, 67, 0.5) !important;
                color: #ffffff !important;
            }

            .case-status-badge.badge-light-info {
                background: rgba(0, 207, 232, 0.3) !important;
                border-color: rgba(0, 207, 232, 0.5) !important;
                color: #ffffff !important;
            }

            .case-status-badge.badge-light-success {
                background: rgba(40, 199, 111, 0.3) !important;
                border-color: rgba(40, 199, 111, 0.5) !important;
                color: #ffffff !important;
            }
        }
    }

    .case-card-body {
        padding: 1.25rem;

        .amount-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.4rem 0;

            .amount-label {
                color: #6e6b7b;
                font-size: 0.855rem;
                display: inline-flex;
                align-items: center;
                gap: 0.35rem;
                flex-wrap: wrap;

                .payment-plan-label {
                    font-size: 0.765rem;
                    color: #00cfe8;
                    font-weight: 500;
                }

                .info-icon {
                    color: #a0a0a0;
                    cursor: help;
                    display: inline-flex;
                    align-items: center;

                    &:hover {
                        color: #088c76;
                    }
                }
            }

            .amount-value {
                font-weight: 500;
                color: #5e5873;
                font-size: 0.9rem;
            }

            &.total-row {
                margin-top: 0.25rem;

                .amount-label {
                    font-weight: 600;
                    color: #5e5873;
                    font-size: 0.9rem;
                }

                .amount-value {
                    font-weight: 700;
                    font-size: 1rem;
                }
            }
        }

        .statements-pending {
            padding: 1.5rem 0.5rem;
            text-align: center;
            color: #6e6b7b;
            font-size: 0.9rem;
            font-style: italic;
            line-height: 1.5;
        }

        .amount-divider {
            margin: 0.75rem 0;
            border-color: rgba(0, 0, 0, 0.1);
        }

        .tooltip-content {
            text-align: left;
            font-size: 0.72rem;
            line-height: 1.4;
        }
    }

    .case-card-footer {
        padding: 1rem 1.25rem;
        background: rgba(0, 0, 0, 0.03);
        border-top: 1px solid rgba(0, 0, 0, 0.08);
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;

        .action-btn {
            flex: 1;
            min-width: 100px;
            font-size: 0.8rem;
            padding: 0.7rem 0.5rem;
            white-space: nowrap;

            svg {
                width: 14px;
                height: 14px;
            }
        }

        &.closed-footer {
            background: rgba(40, 199, 111, 0.08);
            border-top-color: rgba(40, 199, 111, 0.15);
        }

        .closed-label {
            display: flex;
            align-items: center;
            width: 100%;
            font-size: 0.855rem;
            font-weight: 500;
            color: #28c76f;
        }
    }
}
</style>
